import React, { createContext, useContext } from "react";
import { createPortal } from "react-dom";

import type { CustomFieldType } from "#app/lib/data";

// ---------------------------------------------------------------------------
// Customize view panel slots
// ---------------------------------------------------------------------------
//
// The panel's header block is the title row plus an optional RUNG the page
// supplies — the Fields page's search well, the Filters page's mode strip.
// The rung belongs to the header, not the list: it must stay put while the
// list scrolls under it. But its state (the query, the mode tabs) lives in
// the body each page renders, two owners away from the shell that draws the
// header. So the shell exposes a host node and the body portals its rung into
// it — React contexts still flow from the body, the node lands above the
// scroll region.
// ---------------------------------------------------------------------------

/**
 * The Customize view panel provides this ref so it can put the caret in the
 * Fields page's search on arrival. Absent (a fields editor mounted outside
 * the panel), the search is unmanaged.
 */
export const FieldsSearchInputContext =
  createContext<React.RefObject<HTMLInputElement | null> | null>(null);

// ---------------------------------------------------------------------------
// The panel's triggers
// ---------------------------------------------------------------------------
//
// The desktop panel is non-modal and closes on a press anywhere on the page.
// A control that opens or toggles the panel is on the page too, so its press
// would first close the panel as an outside press and then, as a click, do
// what it does — a toggle would close and reopen, an opener would open a
// page the press just closed. The shell reads this marker off the pressed
// element and lets the control's own handler decide instead.
// ---------------------------------------------------------------------------

export const OPTIONS_PANEL_TRIGGER_ATTR = "data-options-panel-trigger";

/** Spread onto every page-level control that opens or toggles a panel. A
 *  control inside a menu or popover needs no marker: a press inside another
 *  popup layer never counts as a press on the page. */
export const OPTIONS_PANEL_TRIGGER_PROPS = {
  [OPTIONS_PANEL_TRIGGER_ATTR]: "",
} as const;

export const OptionsPanelHeaderRungContext = createContext<{
  host: HTMLDivElement | null;
  setHost: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
} | null>(null);

/**
 * Moves a page's header rung out of the scrolling body into the shell's
 * header block. Outside a shell (a page body mounted on its own) the rung
 * renders in place.
 */
export function OptionsPanelHeaderRung({
  children,
}: {
  children: React.ReactNode;
}): React.ReactNode {
  const portal = useContext(OptionsPanelHeaderRungContext);
  if (!portal) return children;
  if (!portal.host) return null;
  return createPortal(children, portal.host);
}

// ---------------------------------------------------------------------------
// The Fields page's steps
// ---------------------------------------------------------------------------
//
// The Fields page is two screens deep: the picker it opens on (`browse` —
// field types to create under one tab, the view's own roster under the
// other) and the create form a type opens (`create`). The header changes
// with the step — the title, where Back goes, whether the Custom Field
// Manager action shows — and the header is the shell's, while the body is
// the editor's. So the shell owns the step and provides it here: it draws
// the header for the step, and the editor renders the body for it. Reached
// from any host of the shell (a saved view's panel, the map's), never
// restated per editor.
// ---------------------------------------------------------------------------

/** The picker's two tabs: the view's own roster (the default), field types
 *  to create. */
export type FieldsPickerTab = "create" | "existing";

export type FieldsPageStep =
  | {
      kind: "browse";
      /** The tab the picker opens on. Absent, the picker picks its own
       *  default; the create step sends the reader back to `existing`, where
       *  the field they made now sits. */
      tab?: FieldsPickerTab;
      /** A field the create step just made, for the editor to show once its
       *  column exists. The editor clears it after — it lives on the step,
       *  not in the editor, because the body remounts on the way back to
       *  the roster and would lose anything it held itself. */
      createdFieldId?: string;
    }
  | { kind: "create"; type: CustomFieldType };

export const BROWSE_STEP: FieldsPageStep = { kind: "browse" };

export type FieldsPageStepValue = {
  step: FieldsPageStep;
  setStep: (step: FieldsPageStep) => void;
  /** The create form reports whether it holds anything typed — the shell
   *  asks before a close or a Back would throw it away. The form reports
   *  `false` again as it unmounts. */
  reportDirty: (dirty: boolean) => void;
};

export const FieldsPageStepContext = createContext<FieldsPageStepValue | null>(
  null,
);

/** The Fields page's current step and its setter. Null outside a shell — an
 *  editor mounted on its own has no header to change, so it offers no
 *  add-field flow. */
export function useFieldsPageStep(): FieldsPageStepValue | null {
  return useContext(FieldsPageStepContext);
}
